/**
 * KakaoShareSection -- 카카오톡 공유 설정 + 커스텀 URL 슬러그
 */

import { useEffect, useRef, useState } from 'react'
import { useInvitationStore } from '../../../stores/invitationStore.js'
import { apiFetch } from '../../../lib/api.js'
import { Input, Textarea, Text } from '../../ui/index.js'
import styles from './SectionForm.module.css'

/** 슬러그 입력값을 소문자 + 하이픈만 허용하도록 정규화 */
function normalizeSlug(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, '')
    .replace(/--+/g, '-')
}

export function KakaoShareSection() {
  const invitation = useInvitationStore((s) => s.invitation)
  const updateNested = useInvitationStore((s) => s.updateNested)
  const updateField = useInvitationStore((s) => s.updateField)

  const [slugStatus, setSlugStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  if (!invitation) return null

  const share = (invitation as any).share ?? {}
  const slug = (invitation as any).slug ?? ''

  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalized = normalizeSlug(e.target.value)
    updateField('slug' as any, normalized || null)

    // 디바운스 중복 검사
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (!normalized || normalized.length < 3) {
      setSlugStatus('idle')
      return
    }

    setSlugStatus('checking')
    debounceRef.current = setTimeout(async () => {
      const res = await apiFetch<{ available: boolean }>(
        `/api/invitations/check-slug?slug=${normalized}&exclude=${invitation.id}`,
      )
      if (res.success) {
        setSlugStatus(res.data.available ? 'available' : 'taken')
      } else {
        setSlugStatus('idle')
      }
    }, 300)
  }

  const origin = window.location.origin
  const shareUrl = slug
    ? `${origin}/inv/${slug}`
    : `${origin}/inv/${invitation.id}`

  return (
    <div className={styles.form}>
      <div>
        <Input
          label="커스텀 URL"
          value={slug}
          onChange={handleSlugChange}
          placeholder="minjun-yuna"
        />
        <Text variant="caption" style={{ marginTop: '0.25rem', color: 'var(--ds-color-text-tertiary)' }}>
          공유 URL: {shareUrl}
        </Text>
        {slugStatus === 'checking' && (
          <Text variant="caption" style={{ marginTop: '0.25rem', color: 'var(--ds-color-text-secondary)' }}>
            확인 중...
          </Text>
        )}
        {slugStatus === 'available' && (
          <Text variant="caption" style={{ marginTop: '0.25rem', color: 'var(--ds-color-text-success, #16a34a)' }}>
            사용 가능한 URL입니다
          </Text>
        )}
        {slugStatus === 'taken' && (
          <Text variant="caption" style={{ marginTop: '0.25rem', color: 'var(--ds-color-text-danger, #dc2626)' }}>
            이미 사용 중인 URL입니다
          </Text>
        )}
      </div>
      <Input
        label="OG 이미지 URL"
        value={share.ogImage ?? ''}
        onChange={(e) => updateNested('share.ogImage', e.target.value)}
        placeholder="https://..."
      />
      <Input
        label="공유 제목"
        value={share.ogTitle ?? ''}
        onChange={(e) => updateNested('share.ogTitle', e.target.value)}
        placeholder="OO & OO 결혼합니다"
      />
      <Textarea
        label="공유 설명"
        value={share.ogDescription ?? ''}
        onChange={(e) => updateNested('share.ogDescription', e.target.value)}
        placeholder="두 사람이 하나 되는 날, 함께 축복해 주세요"
        rows={3}
      />
    </div>
  )
}
